import { useCallback, useRef } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowDown, ArrowUp, ChevronsUpDown, Monitor, Apple, Terminal } from 'lucide-react';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { TagChip } from '@/components/ui/Badge';
import { formatAbsolute, formatRelativeShort } from '@/lib/format';
import { cn } from '@/lib/cn';
import type { DeviceListRow, DevicePlatform } from '@/lib/api/types';
import type { DeviceSortKey, SortDirection } from '@/lib/api/endpoints/devices';

/*
  The device table.

  KEYBOARD NAVIGATION. This is the requirement that shaped the markup, so it is written
  down rather than left to be discovered.

  * It is a real <table> with <caption>, <thead>, <th scope="col"> and <th scope="row">. A
    screen reader in table mode can read "Organization: Acme Dental" for any cell, which a
    div grid cannot do without reimplementing the entire accessibility tree.
  * Sortable headers are <button> elements inside the <th>, and the <th> carries
    aria-sort="ascending" / "descending" / "none". That is the attribute assistive
    technology actually reports; a caret glyph alone conveys nothing.
  * Every row is reachable with Tab, because the device name cell contains a real link.
  * Arrow keys move between rows without leaving the keyboard: ArrowDown and ArrowUp move to
    the next and previous row link, Home and End jump to the first and last. This is handled
    once on the <tbody> rather than per row, and it only intercepts those four keys, so Tab,
    typeahead and the screen reader's own navigation keys are untouched.
  * The sort direction caret is aria-hidden. aria-sort already says it.

  The IP column shows the public address with the local address underneath, because a
  technician needs the public one to correlate with a firewall log and the local one to talk
  to the customer's network. Both in one column beats a twelfth column.
*/

const PLATFORM_ICON: Readonly<Record<DevicePlatform, typeof Monitor>> = {
  windows: Monitor,
  macos: Apple,
  linux: Terminal,
};

export interface SortState {
  readonly key: DeviceSortKey;
  readonly direction: SortDirection;
}

interface ColumnDef {
  readonly key: DeviceSortKey | null;
  readonly label: string;
  readonly className?: string;
  /** Hidden below this breakpoint to keep the table readable on a narrow viewport. */
  readonly hideBelow?: 'sm' | 'md' | 'lg' | 'xl';
}

const COLUMNS: readonly ColumnDef[] = [
  { key: 'name', label: 'Device Name' },
  { key: 'organizationName', label: 'Organization' },
  { key: 'siteName', label: 'Site', hideBelow: 'xl' },
  { key: 'os', label: 'OS', hideBelow: 'lg' },
  { key: 'loggedInUser', label: 'Logged-In User', hideBelow: 'lg' },
  { key: 'state', label: 'Status' },
  { key: 'lastSeenAt', label: 'Last Seen' },
  { key: 'publicIp', label: 'IP', hideBelow: 'xl' },
  { key: 'agentVersion', label: 'Agent', hideBelow: 'md' },
  { key: 'lastTechnicianName', label: 'Last Technician', hideBelow: 'xl' },
  { key: null, label: 'Tags', hideBelow: 'xl' },
];

const HIDE_CLASSES: Readonly<Record<NonNullable<ColumnDef['hideBelow']>, string>> = {
  sm: 'hidden sm:table-cell',
  md: 'hidden md:table-cell',
  lg: 'hidden lg:table-cell',
  xl: 'hidden xl:table-cell',
};

function ariaSortValue(column: ColumnDef, sort: SortState): 'ascending' | 'descending' | 'none' {
  if (column.key === null || column.key !== sort.key) {
    return 'none';
  }
  return sort.direction === 'asc' ? 'ascending' : 'descending';
}

/**
 * The header sort control. Split out so the column key is narrowed to a non-null
 * DeviceSortKey at the boundary rather than inside a closure, where TypeScript cannot keep
 * the narrowing.
 */
function SortButton({
  label,
  sortKey,
  sort,
  onSort,
}: {
  readonly label: string;
  readonly sortKey: DeviceSortKey;
  readonly sort: SortState;
  readonly onSort: (key: DeviceSortKey) => void;
}): React.JSX.Element {
  const active = sortKey === sort.key;
  return (
    <button
      type="button"
      onClick={() => {
        onSort(sortKey);
      }}
      className="inline-flex items-center gap-1 rounded text-inherit hover:text-fg"
    >
      {label}
      {active ? (
        sort.direction === 'asc' ? (
          <ArrowUp className="size-3 text-accent" aria-hidden="true" />
        ) : (
          <ArrowDown className="size-3 text-accent" aria-hidden="true" />
        )
      ) : (
        <ChevronsUpDown className="size-3 opacity-40" aria-hidden="true" />
      )}
    </button>
  );
}

export interface DeviceTableProps {
  readonly rows: readonly DeviceListRow[];
  readonly sort: SortState;
  readonly onSortChange: (next: SortState) => void;
  /** Dimmed while a refetch is in flight, so stale rows are visibly stale. */
  readonly refreshing?: boolean;
}

export function DeviceTable({
  rows,
  sort,
  onSortChange,
  refreshing = false,
}: DeviceTableProps): React.JSX.Element {
  const bodyRef = useRef<HTMLTableSectionElement>(null);

  const handleSortClick = useCallback(
    (key: DeviceSortKey): void => {
      onSortChange(
        key === sort.key
          ? { key, direction: sort.direction === 'asc' ? 'desc' : 'asc' }
          : // A new column starts ascending, except time, where "most recent first" is what
            // anyone actually wants on the first click.
            { key, direction: key === 'lastSeenAt' ? 'desc' : 'asc' },
      );
    },
    [onSortChange, sort.direction, sort.key],
  );

  const handleBodyKeyDown = useCallback((event: React.KeyboardEvent<HTMLTableSectionElement>): void => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      return;
    }
    const container = bodyRef.current;
    if (container === null) {
      return;
    }
    const links = [...container.querySelectorAll<HTMLElement>('[data-row-anchor]')];
    if (links.length === 0) {
      return;
    }
    const current = links.findIndex((link) => link === document.activeElement);
    let nextIndex: number;
    switch (event.key) {
      case 'ArrowDown':
        nextIndex = current === -1 ? 0 : Math.min(current + 1, links.length - 1);
        break;
      case 'ArrowUp':
        nextIndex = current === -1 ? links.length - 1 : Math.max(current - 1, 0);
        break;
      case 'Home':
        nextIndex = 0;
        break;
      default:
        nextIndex = links.length - 1;
        break;
    }
    event.preventDefault();
    links[nextIndex]?.focus();
  }, []);

  return (
    <div className="overflow-x-auto">
      <table
        className={cn(
          'w-full min-w-[52rem] border-collapse text-left text-sm transition-opacity',
          refreshing && 'opacity-60',
        )}
      >
        <caption className="sr-only">
          Enrolled devices. Use the column header buttons to sort. Within the table body, arrow
          up and arrow down move between rows, Home and End jump to the first and last row.
        </caption>
        <thead>
          <tr className="border-b border-border bg-surface-sunken">
            {COLUMNS.map((column) => (
              <th
                key={column.label}
                scope="col"
                aria-sort={ariaSortValue(column, sort)}
                className={cn(
                  'px-3 py-2 text-xs font-semibold tracking-wide text-fg-muted uppercase',
                  column.hideBelow === undefined ? undefined : HIDE_CLASSES[column.hideBelow],
                )}
              >
                {column.key === null ? (
                  column.label
                ) : (
                  <SortButton
                    label={column.label}
                    sortKey={column.key}
                    sort={sort}
                    onSort={handleSortClick}
                  />
                )}
              </th>
            ))}
          </tr>
        </thead>

        <tbody ref={bodyRef} onKeyDown={handleBodyKeyDown}>
          {rows.map((row) => {
            const { device, presence } = row;
            const PlatformIcon = PLATFORM_ICON[device.platform];
            const name = device.displayName ?? device.hostname;
            return (
              <tr key={device.id} className="border-b border-border last:border-b-0 hover:bg-surface-hover">
                <th scope="row" className="max-w-56 px-3 py-2 font-normal">
                  {/*
                    A router Link, not an anchor to a fragment. This used to be
                    href={`#device-${id}`}, which looked like a link, moved nowhere, and could
                    not be copied into a ticket. data-row-anchor is what the arrow key handler
                    on the tbody collects, so the keyboard navigation above keeps working.
                  */}
                  <Link
                    to="/devices/$deviceId"
                    params={{ deviceId: device.id }}
                    data-row-anchor="true"
                    className="flex items-start gap-2 rounded text-left"
                  >
                    <PlatformIcon
                      className="mt-0.5 size-3.5 shrink-0 text-fg-subtle"
                      aria-hidden="true"
                    />
                    <span className="min-w-0">
                      <span className="block truncate font-mono text-[13px] font-medium text-accent">
                        {name}
                      </span>
                      <span className="sr-only">{`, platform ${device.platform}`}</span>
                      {device.status !== 'active' ? (
                        <span className="block text-[11px] font-semibold text-[var(--color-warning)] uppercase">
                          {device.status}
                        </span>
                      ) : null}
                    </span>
                  </Link>
                </th>

                <td className="max-w-44 px-3 py-2">
                  <span className="block truncate text-fg">{row.organizationName}</span>
                </td>

                <td className={cn('max-w-36 px-3 py-2', HIDE_CLASSES.xl)}>
                  <span className="block truncate text-fg-muted">{row.siteName ?? 'Unassigned'}</span>
                </td>

                <td className={cn('max-w-44 px-3 py-2', HIDE_CLASSES.lg)}>
                  <span className="block truncate text-fg-muted">
                    {device.osProduct ?? 'Unknown'}
                  </span>
                  <span className="block truncate text-xs text-fg-subtle">
                    {device.osVersion ?? ''}
                    {device.osBuild === null ? '' : ` (${device.osBuild})`}
                  </span>
                </td>

                <td className={cn('max-w-40 px-3 py-2', HIDE_CLASSES.lg)}>
                  {presence?.loggedInUser === null || presence?.loggedInUser === undefined ? (
                    <span className="text-fg-subtle">No interactive user</span>
                  ) : (
                    <>
                      <span className="block truncate font-mono text-[13px] text-fg">
                        {presence.loggedInUser}
                      </span>
                      {presence.userSessionState === null ? null : (
                        <span className="block text-xs text-fg-subtle">
                          {presence.userSessionState.replace('_', ' ')}
                        </span>
                      )}
                    </>
                  )}
                </td>

                <td className="px-3 py-2">
                  <StatusBadge state={presence?.state ?? 'offline'} />
                </td>

                <td className="px-3 py-2 whitespace-nowrap">
                  {presence?.lastSeenAt === null || presence?.lastSeenAt === undefined ? (
                    <span className="text-fg-subtle">Never</span>
                  ) : (
                    <time
                      dateTime={presence.lastSeenAt}
                      title={formatAbsolute(presence.lastSeenAt)}
                      className="text-fg-muted tabular-nums"
                    >
                      {formatRelativeShort(presence.lastSeenAt)}
                      <span className="sr-only">{` ago, ${formatAbsolute(presence.lastSeenAt)}`}</span>
                    </time>
                  )}
                </td>

                <td className={cn('px-3 py-2 whitespace-nowrap', HIDE_CLASSES.xl)}>
                  <span className="block font-mono text-[13px] text-fg-muted">
                    {presence?.publicIp ?? 'Unknown'}
                  </span>
                  <span className="block font-mono text-[11px] text-fg-subtle">
                    {presence?.localIp ?? ''}
                  </span>
                </td>

                <td className={cn('px-3 py-2 whitespace-nowrap', HIDE_CLASSES.md)}>
                  <span className="font-mono text-[13px] text-fg-muted">
                    {device.agentVersion ?? 'Unknown'}
                  </span>
                </td>

                <td className={cn('max-w-36 px-3 py-2', HIDE_CLASSES.xl)}>
                  <span className="block truncate text-fg-muted">
                    {row.lastTechnicianName ?? 'None'}
                  </span>
                </td>

                <td className={cn('max-w-48 px-3 py-2', HIDE_CLASSES.xl)}>
                  {device.tags.length === 0 ? (
                    <span className="text-fg-subtle">None</span>
                  ) : (
                    <span className="flex flex-wrap gap-1">
                      {device.tags.map((tag) => (
                        <TagChip key={tag}>{tag}</TagChip>
                      ))}
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
